/**
 * Privacy policy — LEGALLY VALIDATED TEXT. Do not rephrase.
 * Same rule as cgv.fr.js: transport, not authoring.
 *
 * Belgian bodies keep their French name with an English gloss on first
 * use — Belgium publishes no official English legal nomenclature, and an
 * invented translation would be less traceable than the real name.
 */
const CONFIDENTIALITE_EN = String.raw`
# Privacy policy

**Archer · archer.law**
Last updated: {{DATE_MISE_EN_LIGNE}}

*This document is available in French, Dutch and English. In case of discrepancy between language versions, the French version prevails.*

This policy explains which personal data we process, why, for how long, with whom we share it and what your rights are. It applies to the site archer.law and the associated services ("the **Service**").

## 1. Data controller

**RND CONCIERGE SERVICES - FZCO** (IFZA licence no. 63620), DSO-IFZA, IFZA Properties, Dubai Silicon Oasis, Dubai, United Arab Emirates, represented by Romain Debecq.

General contact and personal data: **bonjour@archer.law**

As Archer is established outside the European Union and offers its services to persons located in the Union, the GDPR applies in full to our processing operations (art. 3(2) GDPR). The designation of a representative in the Union (art. 27 GDPR) is under way; its details will be published here as soon as it is designated.

## 2. The data we process

**Account data**: surname, first name, email address, password (hashed), language, preferences. If you submit a document **without creating an account**, a provisional account is opened to hold your analysis: it holds no email address until you confirm one with a code, and it is **deleted together with your document within 24 hours** if you do not. If you sign in with Google: the profile information transmitted by Google (name, email).

**Documents and case content**: the documents you upload (letters, contracts, tickets, invoices…), the descriptions of your situation, the categories and objectives you fill in. **This content may contain sensitive data** (for example data relating to offences, health or family situation): we collect it only because you choose to send it to us in order to obtain the Service, and we process it exclusively for that purpose.

**Data generated by the Service**: analyses, scores, letters produced, case history, exchanges with the assistant and with the legal experts.

**Billing data**: plan, purchase history, payment status. **We do not store your card numbers**: they are processed directly by our payment provider (Stripe).

**Technical data**: connection logs, IP address, browser type, technical events necessary for security and operation.

**Cookies and trackers**: see the Cookie policy.

## 3. Why and on what legal basis

| Purpose | Legal basis |
|---|---|
| Create and manage your account, provide the Service (analyses, letters, follow-up, chat) | Performance of the contract (art. 6(1)(b) GDPR) |
| Analyse a document submitted **before an account exists**, and send you the code that confirms your address | Your express request for immediate performance, collected through a box that is never pre-ticked at the moment of submission (art. 6(1)(b)); explicit consent for any sensitive data (art. 9(2)(a)) |
| Process case content, including any sensitive data or data relating to offences that you entrust to us | Your explicit request for the service; explicit consent where required (art. 9(2)(a)); establishment, exercise or defence of legal claims (art. 9(2)(f)) |
| Billing, accounting, fraud prevention | Performance of the contract and legal obligations (art. 6(1)(b) and 6(1)(c)) |
| Service emails (confirmations, case progress, security) | Performance of the contract |
| Archer information and offer emails | Legitimate interest for existing customers, with a right to object at any time; consent otherwise |
| Security, abuse prevention, logs | Legitimate interest (art. 6(1)(f)) |
| Advertising and audience measurement through third-party trackers | **Prior consent** (see Cookie policy) |
| Improvement of the Service (aggregated statistics, corrections) | Legitimate interest, on minimised data |

**What we do not do**: we do not sell your data; we do not use the content of your cases for advertising; we do not allow our AI providers to train their models on your content.

## 4. Artificial intelligence

The Service uses AI models supplied by **Anthropic** (United States) to analyse your documents and generate content. Your documents and messages are transmitted to that provider strictly in order to produce the requested analysis, under a contract that prohibits their use for any other purpose, including model training. The analyses produced are automated estimates; they do not constitute a decision producing legal effects taken solely by automated means within the meaning of art. 22 GDPR: you remain the sole decision-maker as to what follows in your case, and a legal expert may intervene depending on your plan.

## 5. Recipients and processors

Your data is processed by the following providers, each for its own function:

| Provider | Role | Location |
|---|---|---|
| Railway | Hosting of the application and the database (MongoDB) | United States / cloud infrastructure |
| Anthropic | AI analysis of documents and content generation | United States |
| Stripe | Payments | EU / United States |
| SendGrid (Twilio) | Sending of service emails | United States |
| Google | Authentication (sign in with Google) | EU / United States |
| Qualified trust service provider (eIDAS) | Registered electronic delivery, when you choose that option | EU |
| TikTok, Meta | Advertising and measurement, **only if you consent** through the cookie banner | EU / United States / other |

The following also have access to the data, within the limits of their duties: Archer's salaried legal experts (on your cases, depending on your plan) and, if you choose to contact a partner lawyer through the marketplace, that lawyer (for the information you agree to send them only; they then become controller of their own processing).

## 6. Transfers outside the European Union

Some providers are located in the United States or host data outside the EU, and Archer itself is established in the United Arab Emirates. These transfers are framed by appropriate safeguards: adequacy decision (in particular the EU-US framework for certified providers) or the European Commission's standard contractual clauses, supplemented by technical measures (encryption in transit and at rest). Details available on request at bonjour@archer.law.

## 7. Retention periods

| Data | Duration |
|---|---|
| Account and cases | For the life of the account |
| **Document submitted without a confirmed account** | **Deleted within 24 hours**, together with the provisional account, if the email address is not confirmed |
| Statistical trace of a submission (timestamps, device type, outcome) | Kept without email address and without IP address: both are erased when the submission is deleted, and after 30 days at the latest |
| After a request to delete the account | Deletion within **30 days**, save as set out below |
| Invoices and accounting records | **7 years** (accounting obligation) |
| Technical and security logs | 12 months maximum |
| Proof of registered delivery | Retention period of the qualified provider, in your evidentiary interest |

Encrypted backup copies may temporarily remain after deletion; they are purged on an automatic cycle.

## 8. Security

Encryption of exchanges (TLS), encryption at rest at our hosts, strict role-based access control, logging, hashed passwords, separation of environments. As no system is infallible, we will notify you, and the competent authority, in the event of a personal data breach presenting a risk to your rights, in accordance with art. 33-34 GDPR.

## 9. Your rights

You have the rights of **access**, **rectification**, **erasure**, **restriction**, **portability** and **objection**, as well as the right to **withdraw your consent** at any time (without affecting prior processing).

To exercise them: **bonjour@archer.law** (or from your Settings for deletion of the account). We reply within one month, extendable by two months for complex requests. Proof of identity may be requested.

You may lodge a complaint with the Belgian **Data Protection Authority** (Autorité de protection des données, APD): www.autoriteprotectiondonnees.be, Rue de la Presse 35, 1000 Brussels, or with the authority of your country of residence.

## 10. Minors

The Service is reserved for persons aged 18 and over. We do not knowingly collect data from minors; any account identified as belonging to a minor will be closed and its data deleted.

## 11. Statistical use of the data

Data relating to the cases handled on the Service (nature of the dispute, jurisdiction, outcome, timeframes, amounts) is used in an **aggregated and anonymised** form for statistical purposes, to improve the Service and to detect recurring issues. Legal basis: legitimate interest (art. 6(1)(f) GDPR), on minimised data.

**No individual data is ever communicated to third parties** in that context. The results of that processing contain neither your identity, nor the content of your documents, nor any element allowing you or your case to be identified.

A **minimum aggregation threshold** is applied: no statistic is produced or published where it would relate to a number of cases too small to guarantee the anonymity of the persons concerned. Below that threshold, no figure is returned.

You may object to that processing at any time (see the "Your rights" section).

## 12. Changes

Any substantial change to this policy will be notified to you (email or notice in the Service) before it takes effect. The online version is authoritative.

---

*Questions, requests, exercise of rights: bonjour@archer.law*
`;

export default CONFIDENTIALITE_EN;
